import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://fortworthdental.com';
const IMPLANT_URL = 'https://dentalimplants.fortworthdental.com';

const PROVIDER = {
  "@type": "Dentist",
  "@id": `${BASE_URL}/#dentist`,
  "name": "Fort Worth Dental",
  "telephone": "(817) 985-3337",
  "url": BASE_URL,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "4620 Citylake Blvd W",
    "addressLocality": "Fort Worth",
    "addressRegion": "TX",
    "postalCode": "76132",
    "addressCountry": "US"
  }
};

const AREAS = ["Fort Worth", "Aledo", "Benbrook", "Westover Hills", "Arlington", "Burleson", "Crowley", "Weatherford"];

// Service schema definitions — every service page gets MedicalProcedure + BreadcrumbList
const SERVICE_SCHEMAS = {
  '/dental-implants': {
    name: 'Dental Implants',
    description: 'Expert dental implant placement by Dr. Mund, Diplomate of the American Board of Oral Implantology. Natural-looking tooth replacement in Fort Worth TX.',
  },
  '/invisalign': {
    name: 'Invisalign Clear Aligners',
    description: 'Invisalign clear aligner treatment in Fort Worth TX. Straighten teeth discreetly with custom-fitted clear aligners.',
  },
  '/cosmetic-dentistry': {
    name: 'Cosmetic Dentistry',
    description: 'Transform your smile with cosmetic dentistry in Fort Worth. Veneers, whitening, bonding and smile makeovers.',
  },
  '/lanap': {
    name: 'LANAP Laser Gum Treatment',
    description: 'FDA-cleared LANAP laser gum disease treatment in Fort Worth. Minimally invasive alternative to gum surgery.',
  },
  '/emergency-dentist': {
    name: 'Emergency Dentist',
    description: 'Emergency dental care in Fort Worth TX. Same-day appointments for toothaches, broken teeth, and dental emergencies.',
  },
  '/first-fit-veneers': {
    name: 'First Fit Veneers',
    description: 'Same-day First Fit Veneers in Fort Worth. Get a brand new smile in as little as 2 hours with digital precision technology.',
  },
  '/bone-grafting': {
    name: 'Bone Grafting',
    description: 'Dental bone grafting procedures in Fort Worth TX to rebuild jawbone for dental implant placement.',
  },
  '/bruxism': {
    name: 'Bruxism Treatment',
    description: 'Teeth grinding and bruxism treatment in Fort Worth. Custom night guards and TMJ solutions.',
  },
  '/composite-fillings': {
    name: 'Composite Fillings',
    description: 'Tooth-colored composite fillings in Fort Worth TX. Natural-looking cavity repair and dental restorations.',
  },
  '/crowns-bridges': {
    name: 'Dental Crowns & Bridges',
    description: 'Custom dental crowns and bridges in Fort Worth TX. Restore damaged or missing teeth with durable restorations.',
  },
  '/dentures': {
    name: 'Dentures',
    description: 'Custom dentures and partial dentures in Fort Worth TX. Comfortable, natural-looking tooth replacement options.',
  },
  '/gingivectomy': {
    name: 'Gingivectomy',
    description: 'Gingivectomy gum reshaping procedure in Fort Worth TX. Treat excessive gum tissue and improve your smile.',
  },
  '/guided-tissue-bone': {
    name: 'Guided Tissue & Bone Regeneration',
    description: 'Advanced guided tissue and bone regeneration in Fort Worth TX for periodontal and implant treatments.',
  },
  '/gum-disease': {
    name: 'Gum Disease Treatment',
    description: 'Comprehensive gum disease treatment in Fort Worth TX. Periodontal care from mild gingivitis to advanced periodontitis.',
  },
  '/laser-dentistry': {
    name: 'Laser Dentistry',
    description: 'Advanced laser dentistry in Fort Worth TX. Minimally invasive dental treatments with faster healing times.',
  },
  '/occlusal-adjustment': {
    name: 'Occlusal Adjustment',
    description: 'Occlusal adjustment and bite correction in Fort Worth TX. Relieve TMJ pain and improve jaw alignment.',
  },
  '/oral-cancer-screening': {
    name: 'Oral Cancer Screening',
    description: 'Oral cancer screening and early detection in Fort Worth TX. Regular screenings for peace of mind.',
  },
  '/osseous-surgery': {
    name: 'Osseous Surgery',
    description: 'Osseous surgery for advanced gum disease in Fort Worth TX. Restore gum and bone health.',
  },
  '/porcelain-veneers': {
    name: 'Porcelain Veneers',
    description: 'Custom porcelain veneers in Fort Worth TX. Transform your smile with thin, durable porcelain shells.',
  },
  '/preventative-dentistry': {
    name: 'Preventative Dentistry',
    description: 'Preventative dental care in Fort Worth TX. Cleanings, exams, sealants and fluoride treatments.',
  },
  '/ridge-augmentation': {
    name: 'Ridge Augmentation',
    description: 'Ridge augmentation procedure in Fort Worth TX. Rebuild the jawbone ridge for implant placement.',
  },
  '/ridge-preservation': {
    name: 'Ridge Preservation',
    description: 'Ridge preservation after tooth extraction in Fort Worth TX. Maintain jawbone structure for future implants.',
  },
  '/root-canals': {
    name: 'Root Canal Treatment',
    description: 'Gentle root canal treatment in Fort Worth TX. Save infected teeth with modern endodontic techniques.',
  },
  '/scaling-root-planing': {
    name: 'Scaling & Root Planing',
    description: 'Deep cleaning scaling and root planing in Fort Worth TX. Treat gum disease below the gum line.',
  },
  '/sedation': {
    name: 'Sedation Dentistry',
    description: 'Sedation dentistry options in Fort Worth TX. Oral sedation and nitrous oxide for anxiety-free dental care.',
  },
  '/teeth-whitening': {
    name: 'Teeth Whitening',
    description: 'Professional teeth whitening in Fort Worth TX. In-office and take-home whitening for a brighter smile.',
  },
};

// Implant center pages
const IMPLANT_SCHEMAS = {
  '/implant-center': {
    name: 'Dental Implant Center',
    description: 'Fort Worth Dental Implant Center. Affordable dental implants with FREE consultation and X-rays.',
  },
  '/implant-center/dental-implants': {
    name: 'Dental Implants',
    description: 'Affordable dental implants at Fort Worth Dental Implant Center.',
  },
  '/implant-center/mini-dental-implants': {
    name: 'Mini Dental Implants',
    description: 'Mini dental implants in Fort Worth. Smaller implants for denture stabilization.',
  },
  '/implant-center/jawbone-grafting': {
    name: 'Jawbone Grafting',
    description: 'Jawbone grafting for dental implants at Fort Worth Dental Implant Center.',
  },
  '/implant-center/cosmetic-dentistry': {
    name: 'Implant Cosmetic Dentistry',
    description: 'Cosmetic dentistry services at Fort Worth Dental Implant Center.',
  },
  '/implant-center/dental-plan': {
    name: 'Dental Plan',
    description: 'Affordable dental plan options at Fort Worth Dental Implant Center.',
  },
};

function buildServiceSchema(path, data) {
  const isImplant = path.startsWith('/implant-center');
  const baseUrl = isImplant ? IMPLANT_URL : BASE_URL;
  const cleanPath = isImplant ? path.replace('/implant-center', '') : path;
  const url = isImplant ? `${baseUrl}${cleanPath}` : `${baseUrl}${path}`;

  const medicalProcedure = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": data.name,
    "description": data.description,
    "url": url,
    "procedureType": "http://schema.org/NoninvasiveProcedure",
    "provider": PROVIDER,
    "areaServed": AREAS.map(a => ({ "@type": "City", "name": a }))
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": data.name, "item": url }
    ]
  };

  return [medicalProcedure, breadcrumb];
}

// Routes that should NOT be indexed even though RouteSchemaInjector mounts
// on them. Any pathname starting with one of these prefixes gets `noindex`.
const NOINDEX_PREFIXES = [
  '/admin',
  '/login',
  '/404',
  '/internal',
];

const RouteSchemaInjector = () => {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, '') || '/';

  // Spanish routes are owned by <EsSEO />. Returning null prevents Helmet
  // child-merge collisions (otherwise og:locale, hreflang links, and the
  // JSON-LD <script> from EsSEO get dropped during dedup).
  if (path === '/es' || path.startsWith('/es/')) {
    return null;
  }

  // Pages that should not be indexed (admin, login, 404 handlers etc.).
  // Returning null lets the page-level <Helmet> (e.g. NotFound's
  // noindex tag) win the deduplication.
  const isNoindex = NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
  if (isNoindex) {
    return null;
  }

  // Determine canonical URL
  const isImplant = path.startsWith('/implant-center');
  let canonicalUrl;
  if (isImplant) {
    const implantPath = path.replace('/implant-center', '') || '';
    canonicalUrl = `${IMPLANT_URL}${implantPath}`;
  } else {
    canonicalUrl = path === '/' ? BASE_URL : `${BASE_URL}${path}`;
  }

  // Find schemas for this route
  const serviceData = SERVICE_SCHEMAS[path] || IMPLANT_SCHEMAS[path];
  const schemas = serviceData ? buildServiceSchema(path, serviceData) : [];

  return (
    <Helmet>
      {/* Every page gets a proper canonical — this is the single source of truth */}
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:url" content={canonicalUrl} />
      {/* Inject page-specific schemas. We intentionally do NOT emit a robots
          meta tag here — page-level <Helmet> (Home, NotFound, ServiceAreaPage
          etc.) owns the robots directive so 404 pages can set noindex
          without a global "index, follow" overriding them. */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default RouteSchemaInjector;
